import React, { useState, useEffect, useMemo, useContext } from "react";
import { Box, Modal, Typography, Button, TextField, Stack, FormControlLabel, Switch } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import dayjs from "dayjs";
import { FeriadosContext } from "../../context/FeriadosContext";

const DetalleTramo = ({ tipo, minutos, salarioBase, porcentaje, colors }) => {
  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;
  const tiempoFormateado = `${horas}:${minutosRestantes.toString().padStart(2, '0')}`;
  const salarioPorHora = Number(salarioBase) / (30 * 8);
  const valorPorMinuto = (salarioPorHora / 60) * (1 + porcentaje / 100);
  const total = valorPorMinuto * minutos;

  return (
    <Box mb={2}>
      <Typography variant="body2" style={{ color: colors.grey[100], marginBottom: '4px' }}>
        {tipo} 
      </Typography>
      <Typography variant="body2" style={{ color: colors.greenAccent[400], marginLeft: '16px' }}>
        {tiempoFormateado} × ₲{Math.round(valorPorMinuto * 60).toLocaleString('es-PY')}/h
      </Typography>
      <Typography variant="body1" style={{ color: colors.blueAccent[300], marginLeft: '16px' }}>
        = ₲{Math.round(total).toLocaleString('es-PY')}
      </Typography>
    </Box>
  );
};

const ModalHorasExtras = ({ open, onClose, onSelectGrupo, selectedRowData }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [inputs, setInputs] = useState({ inicio: "", fin: "", descanso: "", descansoInicio: "", descansoFin: "" });
  const [initialInputs, setInitialInputs] = useState({ inicio: "", fin: "", descanso: "", descansoInicio: "", descansoFin: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [customBreakEnabled, setCustomBreakEnabled] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { feriados } = useContext(FeriadosContext);

  const formatTime = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("HH:mm") : value;
  };

  const buildInputsFromRow = (row) => {
    if (!row) return { inicio: "", fin: "", descanso: "", descansoInicio: "", descansoFin: "" };
    const fechaReferencia = row.fecha || row.prohibido || row.salida;
    const inicio = row.prohibido && dayjs(row.prohibido).isValid()
      ? dayjs(row.prohibido).format("HH:mm")
      : (fechaReferencia ? dayjs(fechaReferencia).format("HH:mm") : "");
    const fin = row.salida && dayjs(row.salida).isValid()
      ? dayjs(row.salida).format("HH:mm")
      : "";
    const descanso = row.minutos_almuerzo != null ? String(row.minutos_almuerzo) : "30";
    const descansoInicio = row.descanso_inicio && dayjs(row.descanso_inicio).isValid()
      ? dayjs(row.descanso_inicio).format("HH:mm")
      : "";
    const descansoFin = row.descanso_fin && dayjs(row.descanso_fin).isValid()
      ? dayjs(row.descanso_fin).format("HH:mm")
      : "";
    return { inicio, fin, descanso, descansoInicio, descansoFin };
  };

  const combineDateAndTime = (dateValue, timeStr) => {
    if (!dateValue || !timeStr) return null;
    const base = dayjs(dateValue).isValid() ? dayjs(dateValue) : dayjs();
    const [h, m] = timeStr.split(":").map((v) => Number(v));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return base.hour(h).minute(m).second(0).millisecond(0);
  };

  const computeDesglose = (row, overrides) => {
    if (!row) return null;

    if (!overrides && row.desglose_horas) {
      return row.desglose_horas;
    }

    const fechaReferencia = row.fecha || row.prohibido || row.salida;
    const fecha = dayjs(fechaReferencia).isValid() ? dayjs(fechaReferencia) : dayjs();
    let entrada = overrides?.inicio
      ? combineDateAndTime(fecha, overrides.inicio)
      : (row.prohibido && dayjs(row.prohibido).isValid() ? dayjs(row.prohibido) : null);
    let salida = overrides?.fin
      ? combineDateAndTime(fecha, overrides.fin)
      : (row.salida && dayjs(row.salida).isValid() ? dayjs(row.salida) : null);

    if (!entrada || !salida) return row.desglose_horas || null;
    if (salida.isBefore(entrada)) {
      salida = salida.add(1, "day");
    }

    const minutosHorario = parseInt(row.minutos_horario, 10) || 480;
    const usarDescansoPersonalizado = overrides?.usarDescansoPersonalizado;
    const minutosDescansoBase = Math.max(0, parseInt(row.minutos_almuerzo, 10) || 0);
    const horaEntradaHorario = row.hora_entrada_horario;

    let entradaAjustada = entrada;
    let inicioJornadaRegular = entrada;
    if (horaEntradaHorario) {
      const [hHorario, mHorario] = horaEntradaHorario.split(":").map((v) => Number(v));
      if (!Number.isNaN(hHorario) && !Number.isNaN(mHorario)) {
        const minutosEntrada = entrada.hour() * 60 + entrada.minute();
        const minutosHorarioEntrada = hHorario * 60 + mHorario;
        const horarioOficial = entrada
          .clone()
          .hour(hHorario)
          .minute(mHorario)
          .second(0)
          .millisecond(0);
        const diferenciaTemprana = minutosHorarioEntrada - minutosEntrada;
        if (diferenciaTemprana > 0 && diferenciaTemprana <= 30) {
          entradaAjustada = entradaAjustada.hour(hHorario).minute(mHorario).second(0).millisecond(0);
        }
        if (diferenciaTemprana > 30) {
          inicioJornadaRegular = horarioOficial;
        }
      }
    }

    const restWindows = [];

    const baseDescansoInicio = row.descanso_inicio && dayjs(row.descanso_inicio).isValid()
      ? dayjs(row.descanso_inicio)
      : null;
    let baseDescansoFin = row.descanso_fin && dayjs(row.descanso_fin).isValid()
      ? dayjs(row.descanso_fin)
      : null;

    if (baseDescansoInicio && baseDescansoFin) {
      if (baseDescansoFin.isBefore(baseDescansoInicio) || baseDescansoFin.isSame(baseDescansoInicio)) {
        baseDescansoFin = baseDescansoFin.add(1, "day");
      }
      restWindows.push({ inicio: baseDescansoInicio, fin: baseDescansoFin, tipo: "base" });
    }

    let customDescansoInicio = null;
    let customDescansoFin = null;
    if (usarDescansoPersonalizado) {
      customDescansoInicio = overrides?.descansoInicio
        ? combineDateAndTime(fecha, overrides.descansoInicio)
        : null;
      customDescansoFin = overrides?.descansoFin
        ? combineDateAndTime(fecha, overrides.descansoFin)
        : null;

      if (customDescansoInicio && customDescansoFin) {
        if (customDescansoFin.isBefore(customDescansoInicio) || customDescansoFin.isSame(customDescansoInicio)) {
          customDescansoFin = customDescansoFin.add(1, "day");
        }
        restWindows.push({ inicio: customDescansoInicio, fin: customDescansoFin, tipo: "extra" });
      }
    }

    const fallbackBaseMinutes = restWindows.some((ventana) => ventana.tipo === "base")
      ? 0
      : minutosDescansoBase;
    let fallbackBaseConsumido = 0;

    const totalMinutosJornada = Math.max(0, salida.diff(entradaAjustada, "minute"));
    if (totalMinutosJornada === 0) {
      return row.desglose_horas || { tipo: "Sin datos", desglose: {} };
    }

    const estaEnDescansoProgramado = (cursor) => restWindows.some((ventana) => {
      const afterStart = cursor.isAfter(ventana.inicio) || cursor.isSame(ventana.inicio);
      const beforeEnd = cursor.isBefore(ventana.fin);
      return afterStart && beforeEnd;
    });

    let minutosRegularesAcumulados = 0;
    let minutosExtras = 0;
    let diurnas = 0;
    let nocturnas = 0;
    let cursor = entradaAjustada.clone();

    while (cursor.isBefore(salida)) {
      const enDescansoProgramado = estaEnDescansoProgramado(cursor);
      const enDescansoFallbackBase = fallbackBaseMinutes > 0
        && !enDescansoProgramado
        && fallbackBaseConsumido < fallbackBaseMinutes;

      if (enDescansoProgramado || enDescansoFallbackBase) {
        if (enDescansoFallbackBase) {
          fallbackBaseConsumido += 1;
        }
        cursor = cursor.add(1, "minute");
        continue;
      }

      if (cursor.isBefore(inicioJornadaRegular)) {
        minutosExtras += 1;
        const horaActual = cursor.hour();
        if (horaActual >= 20 || horaActual < 6) {
          nocturnas += 1;
        } else {
          diurnas += 1;
        }
      } else if (minutosRegularesAcumulados < minutosHorario) {
        minutosRegularesAcumulados += 1;
      } else {
        minutosExtras += 1;
        const horaActual = cursor.hour();
        if (horaActual >= 20 || horaActual < 6) {
          nocturnas += 1;
        } else {
          diurnas += 1;
        }
      }

      cursor = cursor.add(1, "minute");
    }

    const minutosTrabajoTotal = minutosRegularesAcumulados + minutosExtras;
    const esDomingo = fecha.day() === 0;
    const esFeriado = feriados?.some((feriado) => dayjs(feriado.fecha).isSame(fecha, "day"));

    if (esDomingo || esFeriado) {
      return {
        tipo: "Domingo/Feriado",
        desglose: { domingo: minutosTrabajoTotal },
      };
    }

    if (minutosExtras <= 0) {
      return {
        tipo: "Sin horas extras",
        desglose: { normales: minutosTrabajoTotal },
      };
    }

    return {
      tipo: "Mixta",
      desglose: {
        diurnas,
        nocturnas,
      },
    };
  };

  useEffect(() => {
    if (selectedRowData) {
      setSelectedRow(selectedRowData);
      const derivedInputs = buildInputsFromRow(selectedRowData);
      setInitialInputs(derivedInputs);
      setInputs(derivedInputs);
      setEditMode(false);
      setFieldErrors({});
      setCustomBreakEnabled(false);
    }
  }, [selectedRowData]);

  const salarioBaseMensual = useMemo(() => {
    if (!selectedRow) return 0;
    const { salario_real, salario_ips, salario_monto } = selectedRow;
    const resolved = salario_ips ?? salario_real ?? salario_monto ?? 0;
    const parsed = Number(resolved);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [selectedRow]);

  const desgloseActual = useMemo(() => {
    if (!selectedRow) return null;
    const overrides = editMode
      ? {
          ...inputs,
          usarDescansoPersonalizado: customBreakEnabled,
        }
      : null;
    return computeDesglose(selectedRow, overrides);
  }, [selectedRow, editMode, inputs, feriados, customBreakEnabled]);

  const calcularTotal = () => {
    if (!desgloseActual?.desglose) return 0;
    if (!Number.isFinite(salarioBaseMensual)) return 0;
    const desglose = desgloseActual.desglose;
    const salarioMensual = salarioBaseMensual;
    let total = 0;

    // Calcular el salario por minuto base
    const salarioPorMinuto = salarioMensual / (30 * 8 * 60);

    if (desglose.domingo) {
      // Cálculo para domingos (+100%)
      total += (salarioPorMinuto * 2) * desglose.domingo;
    } else {
      if (desglose.diurnas) {
        // Cálculo para minutos diurnos (+50%)
        total += (salarioPorMinuto * 1.5) * desglose.diurnas;
      }
      if (desglose.nocturnas) {
        // Horas extras nocturnas (+130%)
        total += (salarioPorMinuto * 2.6) * desglose.nocturnas;
      }
    }

    return Math.round(total);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setInputs((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const handleToggleEdit = () => {
    if (editMode) {
      setInputs(initialInputs);
      setCustomBreakEnabled(false);
    }
    setEditMode((prev) => !prev);
  };

  const handleToggleCustomBreak = (event) => {
    const enabled = event.target.checked;
    setCustomBreakEnabled(enabled);
    setFieldErrors((prev) => ({
      ...prev,
      descanso: "",
      descansoInicio: "",
      descansoFin: "",
      general: "",
    }));
    if (enabled) {
      setInputs((prev) => ({
        ...prev,
        descanso: "",
        descansoInicio: "",
        descansoFin: "",
      }));
    } else {
      setInputs((prev) => ({
        ...prev,
        descanso: initialInputs.descanso,
        descansoInicio: initialInputs.descansoInicio,
        descansoFin: initialInputs.descansoFin,
      }));
    }
  };

  const totalCalculado = useMemo(() => calcularTotal(), [desgloseActual, selectedRow, salarioBaseMensual]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-horas-extras-title"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
    >
      <Box
        sx={{
          width: 500,
          bgcolor: colors.primary[600],
          p: 3,
          borderRadius: 1,
          boxShadow: 24,
        }}
      >
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" style={{ color: colors.grey[100] }}>
              Detalle de Horas Extras
            </Typography>
            <Button variant="outlined" color="secondary" onClick={handleToggleEdit}>
              {editMode ? "Cancelar" : "Editar horario"}
            </Button>
          </Box>
          {editMode && (
            <Box mb={3}>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="Inicio"
                  type="time"
                  value={inputs.inicio}
                  onChange={handleInputChange("inicio")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldErrors.inicio)}
                  helperText={fieldErrors.inicio}
                />
                <TextField
                  label="Fin"
                  type="time"
                  value={inputs.fin}
                  onChange={handleInputChange("fin")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldErrors.fin)}
                  helperText={fieldErrors.fin}
                />
              </Stack>
              <Box display="flex" alignItems="center" mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      color="secondary"
                      checked={customBreakEnabled}
                      onChange={handleToggleCustomBreak}
                    />
                  }
                  label="Descanso durante horas extra"
                  style={{ color: colors.grey[100] }}
                />
              </Box>
              {customBreakEnabled ? (
                <>
                  <TextField
                    label="Descanso adicional (minutos)"
                    type="number"
                    value={inputs.descanso}
                    onChange={handleInputChange("descanso")}
                    fullWidth
                    InputProps={{ inputProps: { min: 0 } }}
                    error={Boolean(fieldErrors.descanso)}
                    helperText={fieldErrors.descanso}
                  />
                  <Stack direction="row" spacing={2} mt={2}>
                    <TextField
                      label="Inicio descanso"
                      type="time"
                      value={inputs.descansoInicio}
                      onChange={handleInputChange("descansoInicio")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.descansoInicio)}
                      helperText={fieldErrors.descansoInicio}
                    />
                    <TextField
                      label="Fin descanso"
                      type="time"
                      value={inputs.descansoFin}
                      onChange={handleInputChange("descansoFin")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.descansoFin)}
                      helperText={fieldErrors.descansoFin}
                    />
                  </Stack>
                  {fieldErrors.general && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                      {fieldErrors.general}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" sx={{ mt: 2, display: "block", color: colors.grey[300] }}>
                  El descanso habitual del horario se descuenta automáticamente.
                </Typography>
              )}
            </Box>
          )}
          {selectedRow && desgloseActual && (
            <>
              <Typography variant="subtitle1" style={{ color: colors.grey[100], marginBottom: '8px' }}>
                Funcionario: {selectedRow.nombre}
              </Typography>
              <Typography variant="subtitle2" style={{ color: colors.blueAccent[300], marginBottom: '16px' }}>
                Salario Base: ₲ {salarioBaseMensual.toLocaleString('es-PY')} 
              </Typography>
              
              <Box sx={{ bgcolor: colors.primary[400], p: 2, borderRadius: 1, marginBottom: '16px' }}>
                {desgloseActual.desglose.domingo ? (
                  <DetalleTramo
                    tipo="DOMINGOS Y FERIADOS"
                    minutos={desgloseActual.desglose.domingo}
                    salarioBase={salarioBaseMensual}
                    porcentaje={100}
                    colors={colors}
                  />
                ) : (
                  <>
                    {desgloseActual.desglose.diurnas > 0 && (
                      <DetalleTramo
                        tipo="JORNADA DIURNA"
                        minutos={desgloseActual.desglose.diurnas}
                        salarioBase={salarioBaseMensual}
                        porcentaje={50}
                        colors={colors}
                      />
                    )}
                    {desgloseActual.desglose.nocturnas > 0 && (
                      <DetalleTramo
                        tipo="JORNADA NOCTURNA"
                        minutos={desgloseActual.desglose.nocturnas}
                        salarioBase={salarioBaseMensual}
                        porcentaje={160}
                        colors={colors}
                      />
                    )}
                  </>
                )}
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" style={{ color: colors.grey[100] }}>
                  Total a Pagar:
                </Typography>
                <Typography variant="h5" style={{ color: colors.greenAccent[400] }}>
                  ₲ {totalCalculado.toLocaleString('es-PY')}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="flex-end" mt={3}>
                <button
                  onClick={() => {
                    const usuario = selectedRowData;
                    const total = totalCalculado;
                    const extrasMin = (desgloseActual.desglose.domingo || 0)
                      + (desgloseActual.desglose.diurnas || 0)
                      + (desgloseActual.desglose.nocturnas || 0);
                    const entradaConfirmada = editMode && inputs.inicio
                      ? inputs.inicio
                      : formatTime(selectedRow.prohibido);
                    const salidaConfirmada = editMode && inputs.fin
                      ? inputs.fin
                      : formatTime(selectedRow.salida);
                    const descansoInicioConfirmado = editMode && inputs.descansoInicio
                      ? inputs.descansoInicio
                      : formatTime(selectedRow.descanso_inicio);
                    const descansoFinConfirmado = editMode && inputs.descansoFin
                      ? inputs.descansoFin
                      : formatTime(selectedRow.descanso_fin);
                    const descansoMinConfirmadoStr = editMode && customBreakEnabled
                      ? inputs.descanso
                      : String(selectedRow.minutos_almuerzo ?? "");

                    const fechaBase = dayjs(selectedRow?.fecha || selectedRow?.prohibido || selectedRow?.salida || dayjs());
                    const newErrors = {};

                    const descansoMinutosNumero = Number(descansoMinConfirmadoStr);
                    let descansoInicioValido = null;
                    let descansoFinValido = null;

                    if (customBreakEnabled) {
                      if (!descansoInicioConfirmado) {
                        newErrors.descansoInicio = "Completa el inicio del descanso";
                      }
                      if (!descansoFinConfirmado) {
                        newErrors.descansoFin = "Completa el fin del descanso";
                      }
                      if (Number.isNaN(descansoMinutosNumero) || descansoMinutosNumero <= 0) {
                        newErrors.descanso = "El descanso debe ser un número mayor a cero";
                      }

                      descansoInicioValido = descansoInicioConfirmado && combineDateAndTime(fechaBase, descansoInicioConfirmado);
                      descansoFinValido = descansoFinConfirmado && combineDateAndTime(fechaBase, descansoFinConfirmado);

                      if (descansoInicioValido && descansoFinValido) {
                        if (descansoFinValido.isBefore(descansoInicioValido) || descansoFinValido.isSame(descansoInicioValido)) {
                          descansoFinValido = descansoFinValido.add(1, "day");
                        }
                        const diffDescanso = descansoFinValido.diff(descansoInicioValido, "minute");
                        if (!Number.isNaN(descansoMinutosNumero) && diffDescanso !== descansoMinutosNumero) {
                          newErrors.descanso = `La diferencia entre inicio y fin (${diffDescanso} min) debe coincidir con descanso`;
                          newErrors.descansoInicio = newErrors.descansoInicio || "Revisa el horario de descanso";
                          newErrors.descansoFin = newErrors.descansoFin || "Revisa el horario de descanso";
                        }
                      }

                      if (descansoInicioValido) {
                        const minutosHorarioBase = Number(selectedRow.minutos_horario) || 0;
                        const minutosDescansoBase = Number(selectedRow.minutos_almuerzo) || 0;
                        let salidaHorarioHabitual = null;

                        if (selectedRow.hora_entrada_horario) {
                          const [hEntrada, mEntrada] = selectedRow.hora_entrada_horario.split(":").map((v) => Number(v));
                          if (!Number.isNaN(hEntrada) && !Number.isNaN(mEntrada)) {
                            const entradaHorarioBase = fechaBase.clone().hour(hEntrada).minute(mEntrada).second(0).millisecond(0);
                            salidaHorarioHabitual = entradaHorarioBase.add(minutosHorarioBase + minutosDescansoBase, "minute");
                          }
                        }

                        if (!salidaHorarioHabitual && selectedRow.prohibido && dayjs(selectedRow.prohibido).isValid()) {
                          salidaHorarioHabitual = dayjs(selectedRow.prohibido).add(minutosHorarioBase + minutosDescansoBase, "minute");
                        }

                        if (salidaHorarioHabitual && descansoInicioValido.isBefore(salidaHorarioHabitual)) {
                          newErrors.descansoInicio = "El descanso extra debe iniciar después del horario habitual";
                          newErrors.descansoFin = newErrors.descansoFin || "Ajusta el descanso adicional";
                        }
                      }

                      if (!descansoInicioValido || !descansoFinValido) {
                        newErrors.general = "Completa los datos de descanso para continuar";
                      }

                      if (Object.keys(newErrors).length > 0) {
                        setFieldErrors(newErrors);
                        if (!editMode) {
                          setEditMode(true);
                        }
                        return;
                      }
                    }

                    setFieldErrors({});

                    onSelectGrupo({
                      montoCalculado: total,
                      horarioPersonalizado: editMode
                        ? {
                            inicio: inputs.inicio,
                            fin: inputs.fin,
                            descansoMinutos: inputs.descanso,
                            descansoInicio: inputs.descansoInicio,
                            descansoFin: inputs.descansoFin,
                          }
                        : null,
                      resumenCalculo: {
                        minutosExtras: extrasMin,
                        desglose: desgloseActual.desglose,
                      },
                      horariosConfirmados: {
                        entrada: entradaConfirmada,
                        salida: salidaConfirmada,
                        descansoInicio: descansoInicioConfirmado,
                        descansoFin: descansoFinConfirmado,
                        descansoMinutos: String(descansoMinutosNumero),
                      },
                      usuario: {
                        id_empleado: usuario.empleado,
                        nombre: usuario.nombre,
                        salario_monto: usuario.salario_monto,
                        id_grupo: usuario.grupo,
                        id_usuario: usuario.id_usuario,
                        id_salario: usuario.id_salario,
                        id_marcacion: usuario.id_marcacion,
                      },
                    });
                    onClose();
                  }}
                  style={{
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100],
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Confirmar
                </button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalHorasExtras;